import {DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import React, {FC, PropsWithChildren, useMemo} from 'react';

import {isServer} from 'coreUtils/utils';

const DEFAULT_QUERY_CLIENT_OPTIONS = {
	defaultOptions: {
		queries: {
			retry: false,
			staleTime: 60 * 1000,
			refetchOnWindowFocus: false
		},
		mutations: {
			retry: false
		}
	}
};

interface QueryClientSSRProviderProps {
	queryClient?: QueryClient;
	dehydratedState?: DehydratedState;
}

const QueryClientSSRProvider: FC<PropsWithChildren<QueryClientSSRProviderProps>> = ({
	children,
	queryClient: serverQueryClient,
	dehydratedState
}) => {
	const queryClient = useMemo(() => {
		if (isServer && serverQueryClient) {
			return serverQueryClient;
		}

		return new QueryClient(DEFAULT_QUERY_CLIENT_OPTIONS);
	}, []);

	if (isServer) {
		return (
			<QueryClientProvider client={queryClient}>
				{children}
			</QueryClientProvider>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<HydrationBoundary state={dehydratedState}>
				{children}
			</HydrationBoundary>
		</QueryClientProvider>
	);
};

const createQueryClient = (overrides?: Partial<typeof DEFAULT_QUERY_CLIENT_OPTIONS>): QueryClient =>
	new QueryClient({
		...DEFAULT_QUERY_CLIENT_OPTIONS,
		defaultOptions: {
			...DEFAULT_QUERY_CLIENT_OPTIONS.defaultOptions,
			...overrides?.defaultOptions,
			queries: {
				...DEFAULT_QUERY_CLIENT_OPTIONS.defaultOptions.queries,
				...overrides?.defaultOptions?.queries,
			},
			mutations: {
				...DEFAULT_QUERY_CLIENT_OPTIONS.defaultOptions.mutations,
				...overrides?.defaultOptions?.mutations,
			},
		},
	});

export {DEFAULT_QUERY_CLIENT_OPTIONS, createQueryClient};
export default QueryClientSSRProvider;
